import { useState, useEffect, useRef } from 'react';
import { getApiHost, getAuthHeaders } from '../components/Common';
import networkMonitor from '../utils/networkMonitor';

export const useSSE = (endpoint, options = {}) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState({
    text: 'Initializing...',
    connected: false
  });
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const connectionStartTime = useRef(null);
  const lastDataReceived = useRef(null);
  const reconnectAttempts = useRef(0);
  const debugId = useRef(`SSE-${endpoint.replace(/\//g, '-')}-${Date.now()}`);

  const {
    fallbackToPolling = true,
    pollInterval = 10000, // 10 seconds
    onError = null
  } = options;



  useEffect(() => {
    let isMounted = true;

    // Network monitoring
    const networkStatus = networkMonitor.getStatus();

    const networkListener = (event, data) => {
      if (event === 'offline') {
        if (isMounted) {
          setStatus({
            text: 'Network Offline',
            connected: false
          });
          setError('Network connection lost');
        }
      } else if (event === 'online') {
        if (isMounted) {
          // Reset reconnect attempts when network comes back online
          reconnectAttempts.current = 0;
          connectSSE();
        }
      }
    };

    const removeNetworkListener = networkMonitor.addListener(networkListener);

    const connectSSE = () => {
      connectionStartTime.current = Date.now();
      reconnectAttempts.current += 1;

      getApiHost((apiHost) => {
        const url = `${apiHost}${endpoint}`;

        // Check if we have authentication token
        if (!window.saferGoogleToken) {
          console.error('No authentication token available');
          if (fallbackToPolling) {
            startPolling();
          }
          return;
        }

        // Create fetch request with proper authentication
        const requestInit = {
          method: 'GET',
          headers: getAuthHeaders({
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          })
        };

        fetch(url, requestInit)
          .then(async (response) => {
            if (response.status === 401) {
              // Handle authentication error
              window.saferGoogleToken = undefined;
              throw new Error('Authentication failed');
            }

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            if (isMounted) {
              setStatus({
                text: 'Connected - Live Updates',
                connected: true
              });
              setError(null);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // Read the stream
            while (true) {
              if (!isMounted) {
                break;
              }

              const { done, value } = await reader.read();

              if (value) {
                buffer += decoder.decode(value, { stream: !done });
              }
              if (done) {
                buffer += decoder.decode();
              }

              // SSE events are separated by blank lines; keep the last
              // incomplete block in the buffer until more data arrives.
              let eventBlocks = buffer.split('\n\n');
              if (done) {
                buffer = '';
              } else {
                buffer = eventBlocks.pop() || '';
              }
              if (done && buffer.trim()) {
                eventBlocks.push(buffer);
                buffer = '';
              }

              for (const eventBlock of eventBlocks) {
                const dataLines = eventBlock
                  .split('\n')
                  .filter((line) => line.startsWith('data: '));
                const payload = dataLines.map((line) => line.slice(6)).join('\n');
                if (!payload.trim()) {
                  continue;
                }

                try {
                  const parsedData = JSON.parse(payload);
                  lastDataReceived.current = Date.now();

                  if (isMounted) {
                    if (parsedData.error) {
                      setError(parsedData.error);
                      setStatus({
                        text: 'Error - Retrying...',
                        connected: false
                      });
                    } else {
                      setData(parsedData);
                      setError(null);
                      const statusText = parsedData.devices
                        ? `Live - ${parsedData.devices.length} devices`
                        : parsedData.device_id
                          ? 'Connected - Live Updates'
                          : 'Connected - Live Updates';
                      setStatus({
                        text: statusText,
                        connected: true
                      });
                    }
                  }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError);
                }
              }

              if (done) {
                break;
              }
            }
          })
          .catch((error) => {
            console.error('SSE connection error:', error);

            if (isMounted) {
              setStatus({
                text: 'Connection Error - Retrying...',
                connected: false
              });
              setError(error.message);
            }

            // Fallback to polling if SSE fails repeatedly
            if (reconnectAttempts.current >= 3 && fallbackToPolling) {
              startPolling();
              return;
            }

            // Reconnect after 5 seconds
            setTimeout(() => {
              if (isMounted) {
                connectSSE();
              }
            }, 5000);
          });
      });
    };

        const startPolling = () => {
      // Clear any existing polling interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      setStatus({
        text: 'Polling - Updates every 10s',
        connected: true
      });

      let pollCount = 0;
      const fetchData = () => {
        if (!isMounted) {
          return;
        }

        pollCount++;
        const pollStart = performance.now();

        getApiHost(apiHost => {
          const url = `${apiHost}/v1/devices?last_event=true`;

          if (!window.saferGoogleToken) {
            console.error('No authentication token available for polling');
            return;
          }

          const requestInit = {
            method: 'GET',
            headers: getAuthHeaders(),
          };

          fetch(url, requestInit)
            .then(res => {
              const fetchDuration = performance.now() - pollStart;

              if (res.status === 401) {
                window.saferGoogleToken = undefined;
                throw new Error('Authentication failed');
              }
              if (!res.ok) {
                throw new Error('Failed to fetch device data');
              }
              return res.json();
            })
            .then(response => {
              const totalDuration = performance.now() - pollStart;
              lastDataReceived.current = Date.now();

              if (isMounted) {
                setData(response);
                setError(null);
                setStatus({
                  text: `Polling - ${response.devices?.length || 0} devices`,
                  connected: true
                });
              }
            })
            .catch(err => {
              const totalDuration = performance.now() - pollStart;

              if (isMounted) {
                console.error('Error fetching device data:', err);
                setError(err.message);
                setStatus({
                  text: 'Error - Retrying...',
                  connected: false
                });
              }
            });
        });
      };

      // Initial fetch
      fetchData();

      // Set up polling interval
      pollIntervalRef.current = setInterval(fetchData, pollInterval);
    };

        // Start SSE connection
    connectSSE();

        // Cleanup on unmount
    return () => {
      isMounted = false;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      // Remove network listener
      removeNetworkListener();
    };
  }, [endpoint, fallbackToPolling, pollInterval]);

    // Add periodic health check logging
  useEffect(() => {
    const healthCheckInterval = setInterval(async () => {
      const timeSinceLastData = lastDataReceived.current ?
        Date.now() - lastDataReceived.current : 'never';
      const isStale = lastDataReceived.current && (Date.now() - lastDataReceived.current) > 60000; // 1 minute

      // Get current network status
      const networkStatus = networkMonitor.getStatus();

      if (isStale) {
        // Test network connectivity if connection seems stale
        if (networkStatus.isOnline) {
          const connectivityTest = await networkMonitor.testConnectivity();
        }
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(healthCheckInterval);
  }, [status.text, status.connected]);

  return { data, status, error };
};